import React, { useState, useRef } from "react";
import {
  map,
  filter,
  tap,
  catchError,
  Subject,
  takeUntil,
  of,
  switchMap,
} from "rxjs";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import store from "../../state/store";
import AdjustableTextArea from "../atoms/AdjustableTextArea";
import FullNameNotPublic from "./FullNameNotPublic";
import HelperText from "../atoms/HelperText";
import isUserProfilePublic from "../../common/helpers/isUserProfilePublic";
import isUserLoggedIn from "../../common/helpers/isUserLoggedIn";
import SourceEntity from "../Publications/models/SourceEntity";
import AddToast from "../../state/actions/AddToast";
import apiClient from "../../services/apiClient";
import Comment from "../../models/Comment";
import isPortal from "../../common/helpers/isPortal";
import Topic from "../../models/Topic";
import {
  getUserFullName,
  getCurrentUserSsoId,
} from "../../state/selectors/general.selectors";

function validate(strVal) {
  const smallestLen = 5;
  const biggestLen = 32000;

  if (strVal.length < smallestLen) {
    return `Minimum comment length: ${smallestLen} characters`;
  }
  if (strVal.length > biggestLen) {
    return `Maximum comment length: ${biggestLen} characters`;
  }
  return null;
}

function getPlaceholder(mainItem, subType) {
  const typeName = subType ? subType.replace(/s$/, "") : "";
  return subType
    ? `Post your comment regarding this ${typeName} and ${mainItem}`
    : `Post your comment for ${mainItem}`;
}

const handleError = () => {
  AddToast("Error posting comment");
  return of(null);
};

function CommentForm({
  topic,
  className,
  sourceEntity,
  slug,
  subType,
  comment,
  ssoId,
  userFullName,
  onSuccess,
}) {
  const [val, setVal] = useState(comment?.comment);
  const destroy$ = useRef(new Subject()).current;
  const [isLoading, setIsLoading] = useState(false);
  const replyFormRef = useRef();

  const isPublic = isUserProfilePublic();
  const isLoggedIn = isUserLoggedIn();
  const error = validate(val || "");

  const placeHolder = getPlaceholder(sourceEntity?.description, subType);

  const handleSuccess = (obj, id) => {
    const { comments } = topic;
    const x = new Comment({ ...obj, user_sso_id_hashed: ssoId }, slug);
    const newComments = id
      ? comments.map((c) => (c.id === id ? x : c))
      : [x, ...comments];

    const newTopic = topic.setComments(newComments).clone();
    const { threadMap: current } = store.getState() || {};
    const threadMap = current.set(slug, newTopic);
    store.dispatch({ type: "SET_THREAD_MAP", threadMap });
  };

  const handleSubmit = (str) => {
    if (isLoading) return () => {};
    of(null)
      .pipe(
        tap(() => setIsLoading(true)),
        switchMap(() => apiClient.postComment(slug, str, comment?.id)),
        map((x) => x?.result),
        filter(Boolean),
        tap((x) => handleSuccess(x.post || x, comment?.id)),
        catchError(handleError),
        tap(() => setIsLoading(false)),
        tap(() => setVal("")),
        tap((x) => x && onSuccess()),
        takeUntil(destroy$)
      )
      .subscribe();

    return () => destroy$.next();
  };

  if (isPortal()) return null;

  return isPublic && isLoggedIn ? (
    <div className="tw-relative">
      <div className="tw-flex tw-gap-1 tw-items-center tw-justify-start">
        <div className="tw-font-bold">{userFullName}</div>
        <HelperText
          title=""
          anchor={
            <span className="tw-text-sm tw-text-blue-400 hover:tw-opacity-100 tw-opacity-80">
              <i className="fa fa-info-circle" />
            </span>
          }
          customText={`Your comment will be public. Please do not enter personally identifiable information for any individual.
        It will be displayed using your real name: ${userFullName}`}
        />
      </div>

      <div className="tw-relative tw-flex tw-w-full tw-items-center tw-justify-start">
        <div ref={replyFormRef} className="tw-relative tw-flex-grow">
          <AdjustableTextArea
            className={className}
            placeHolder={placeHolder}
            onChange={setVal}
            value={val}
          />
          {isLoading ? (
            <div className="tw-absolute tw-inset-0 loading-element tw-rounded-lg tw-opacity-40" />
          ) : null}
        </div>

        <div
          data-tooltip={error}
          data-tooltip-delay={300}
          className="tw-inline-block"
        >
          <button
            type="submit"
            className={`${
              !error
                ? "tw-bg-primary tw-border tw-border-primary"
                : "tw-bg-coolGray-300 tw-border tw-pointer-events-none tw-cursor-not-allowed"
            } tw-px-2 tw-ml-2 tw-py-1 tw-font-semibold tw-rounded-sm tw-text-white tw-text-sm`}
            onClick={() => handleSubmit(val)}
            disabled={error}
          >
            Post
          </button>
        </div>
      </div>
    </div>
  ) : (
    <FullNameNotPublic />
  );
}

CommentForm.propTypes = {
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  comment: PropTypes.instanceOf(Comment),
  topic: PropTypes.instanceOf(Topic),
  onSuccess: PropTypes.func,
  slug: PropTypes.string,
  className: PropTypes.string,
  userFullName: PropTypes.string,
  ssoId: PropTypes.string,
  subType: PropTypes.string,
};

CommentForm.defaultProps = {
  onSuccess: () => {},
  topic: null,
  comment: null,
  className: "",
  slug: "",
  sourceEntity: null,
  userFullName: "",
  ssoId: "",
  subType: "",
};

const mapStateToProps = (state) => {
  return {
    userFullName: getUserFullName(state),
    ssoId: getCurrentUserSsoId(state),
  };
};

export default connect(mapStateToProps, {})(CommentForm);
